import React from 'react';
import { useApolloClient } from '@apollo/client';
import { AsyncSelect } from '../ReactSelect';
import { getWaterServiceSearchFilter } from '@/modes/waterSupplier/pages/waterServices/WaterServices';
import { defaultFilters as defaultWaterServiceFilters } from '@/modes/waterSupplier/pages/waterServices/FilterBarWaterServices';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { gql } from '@/modes/waterSupplier/gql';
import { ResultOf } from 'gql.tada';

const GET_WATER_SERVICE_OPTIONS = gql(`
  query GetWaterServiceOptions(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    waterServices(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
      groupBy: ["id"]
    ) {
      id
      siteNumber
      type
      meterNumber
      meterSize
      address(required: true) {
        street1
        street2
      }
      backflowAssemblies {
        id
        model {
          name
          backflowAssemblyMake {
            name
          }
        }
        size
        serialNumber
        testAnniversary
        lastTestDate
        lastTest {
          jobId
          initialResult
          finalResult
        }
        status
        testStatus
        location
        type
        hazardType
        hazardLevel
      }
    }
  }
`);

type WaterServiceOptionItem = ResultOf<
  typeof GET_WATER_SERVICE_OPTIONS
>['waterServices'][number];

type WaterSupplierPickerProps = {
  waterServiceId?: string | null;
  onChange: (waterService: WaterServiceOptionItem) => void;
  placeholder?: string;
  isClearable?: boolean;
};

const WaterServicePicker = ({
  onChange,
  placeholder,
  isClearable,
}: WaterSupplierPickerProps): JSX.Element => {
  const client = useApolloClient();

  const loadOptionsFunc = async (inputValue: string) => {
    const res = await client.query({
      query: GET_WATER_SERVICE_OPTIONS,
      variables: {
        where: inputValue
          ? {
              ...defaultWaterServiceFilters,
              ...getWaterServiceSearchFilter(inputValue),
            }
          : defaultWaterServiceFilters,
        limit: 10,
        offset: 0,
        orderBy: [],
      },
    });

    if (res.data?.waterServices) {
      return res.data.waterServices;
    }

    return [];
  };

  return (
    <AsyncSelect
      cacheOptions
      isMulti={false}
      loadOptions={loadOptionsFunc}
      defaultOptions
      isClearable={isClearable}
      noOptionsMessage={() => null}
      menuPlacement="auto"
      onChange={(selected: WaterServiceOptionItem | null) => {
        if (selected) {
          onChange(selected);
        }
      }}
      placeholder={placeholder ?? 'Select water service...'}
      getOptionLabel={getOptionLabel}
      getOptionValue={(waterService) => waterService.id}
    />
  );
};

const getOptionLabel = (waterService: WaterServiceOptionItem) => {
  const address =
    waterService.address?.street1 +
    (waterService.address?.street2 ? ', ' + waterService.address?.street2 : '');
  const service = waterService.siteNumber;
  const type = waterService.type
    ? ' - ' + constantToLabel(waterService.type)
    : '';
  return `${address} (${service}${type})`;
};

export default WaterServicePicker;
